import { cva, type VariantProps } from 'class-variance-authority'
import { CSSProperties, HTMLInputTypeAttribute, InputHTMLAttributes, ReactNode } from 'react'
import { useFormContext } from 'react-hook-form'
import { cn } from '../../../utils/ClassName'

const containerStyles = cva('relative flex w-full items-center overflow-hidden border transition-colors', {
  defaultVariants: {
    intent: 'default',
    rounded: 'pill',
    size: 'md'
  },
  variants: {
    intent: {
      default: 'border-black/30 bg-white',
      subtle: 'border-[#E2E4E5] bg-[#F8F8F8]',
      danger: 'border-rose-500 bg-white'
    },
    rounded: {
      md: 'rounded-md',
      lg: 'rounded-xl',
      pill: 'rounded-full'
    },
    size: {
      sm: 'h-9',
      md: 'h-11',
      lg: 'h-12'
    }
  }
})

const inputStyles = cva('w-full bg-transparent px-4 h-full text-sm outline-none placeholder:text-gray-400')

type InputBaseProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & VariantProps<typeof containerStyles>

interface FormInputFieldProps extends InputBaseProps {
  style?: CSSProperties
  label?: string
  errorMessage?: string
  type?: HTMLInputTypeAttribute | undefined
  name: string
  suffix?: string | ReactNode
  counter?: boolean
  labelClassName?: string
  no_border?: boolean
  borderRadius?: string
  inputClassName?: string
}

export const FormInputField = ({
  placeholder = 'type here',
  label,
  errorMessage,
  type,
  suffix,
  name = 'name',
  counter = false,
  labelClassName,
  no_border,
  intent,
  rounded,
  size,
  className,
  inputClassName,
  style,
  disabled,
  ...props
}: FormInputFieldProps) => {
  const {
    register,
    setValue,
    getValues,
    formState: { errors }
  } = useFormContext()

  const errorText = errors[name]?.message
  const displayError = errorText && typeof errorText === 'string' ? errorText : errorMessage

  const handleIncrement = () => {
    const currentValue = getValues(name)
    setValue(name, (parseInt(currentValue) || 0) + 1)
  }

  const handleDecrement = () => {
    const currentValue = getValues(name)
    const newValue = (parseInt(currentValue) || 0) - 1
    if (newValue >= 0) {
      setValue(name, newValue)
    }
  }

  return (
    <div>
      {label && <p className={cn('mb-[2px] text-sm font-medium', labelClassName)}>{label}</p>}
      <div className="relative flex w-full items-center">
        <div
          className={cn(containerStyles({ intent, rounded, size }), className)}
          style={{
            borderWidth: no_border ? '0px' : '1px',
            ...(style || {})
          }}
        >
          {counter && !disabled && (
            <button className="px-3 text-base" onClick={handleDecrement} type="button">
              -
            </button>
          )}
          <input
            defaultValue={counter ? 0 : ''}
            disabled={disabled}
            onFocus={e => {
              e.target.addEventListener(
                'wheel',
                function (wheelEvent) {
                  wheelEvent.preventDefault()
                },
                { passive: false }
              )

              if (counter && e.currentTarget.value === '0') {
                setValue(name, '')
              }
            }}
            placeholder={placeholder}
            type={counter ? 'number' : type || 'text'}
            {...register(name)}
            {...props}
            className={cn(inputStyles(), counter && 'text-center', inputClassName)}
          />
          {counter && !disabled && (
            <button className="px-3 text-base" onClick={handleIncrement} type="button">
              +
            </button>
          )}
        </div>
        {suffix && (
          <span className="absolute inset-y-0 right-4 flex items-center justify-center text-sm opacity-30">
            {suffix}
          </span>
        )}
      </div>
      {displayError && <p className="pl-3 mt-1 text-sm text-rose-600">{displayError}</p>}
    </div>
  )
}
